import React, { useState, useEffect } from 'react';
import useThicknessStore from '../../stores/useThicknessStore';
import useWorkflowStore from '../../stores/useWorkflowStore';
import DynamicSlider from '../ui/DynamicSlider';
import { useToolManager } from '../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../constants/tools';
import useEditorStore from '../../stores/useEditorStore';
import styles from './ToolCard.module.css';

const MIN_THICKNESS = 0;
const MAX_THICKNESS = 10;

const ThicknessToolCard = () => {
  const thicknessState = useThicknessStore((state) => state.thickness);
  const setThickness = useThicknessStore((state) => state.setThickness);
  const setTopThickness = useThicknessStore((state) => state.setTopThickness);
  const setOffset = useThicknessStore((state) => state.setOffset);
  const isApplied = useThicknessStore((state) => state.isThicknessApplied);
  const isAppliedFromDraft = useThicknessStore((state) => state.isThicknessAppliedFromDraft);
  const undoCommand = useThicknessStore((state) => state.currentThicknessCommand);
  const { run } = useToolManager();

  const isLoadedFromDraft = isApplied && isAppliedFromDraft;

  // Get selected socket type from workflow store
  const selectedSocketType = useWorkflowStore((state) => state.selectedSocketType);

  // Use local state for UI but sync with store
  const [thicknessSettings, setThicknessSettings] = useState(thicknessState);
  const [thicknessValue, setThicknessValue] = useState(thicknessState.value);
  const [topThicknessValue, setTopThicknessValue] = useState(thicknessState.topValue);
  const [offsetValue, setOffsetValue] = useState(thicknessState.offset);

  // Check if mould is selected to determine slider layout
  const isMouldSelected = selectedSocketType === 'mould';

  // Sync with store when component mounts or store changes
  useEffect(() => {
    setThicknessSettings(thicknessState);
    setThicknessValue(thicknessState.value);
    setTopThicknessValue(thicknessState.topValue);
    setOffsetValue(thicknessState.offset);
  }, [thicknessState]);

  const handleSliderChange = (value) => {
    setThicknessValue(value);
    setThicknessSettings(prev => ({ ...prev, value }));
    setThickness({ value });
  };

  const handleTopSliderChange = (value) => {
    setTopThicknessValue(value);
    setThicknessSettings(prev => ({ ...prev, topValue: value }));
    setTopThickness(value);
  };

  const handleOffsetSliderChange = (value) => {
    setOffsetValue(value);
    setThicknessSettings(prev => ({ ...prev, offset: value }));
    setOffset(value);
  };

  const onApply = () => {
    if (!isApplied) {
      run({ tool: TOOLS_CONFIG.MAIN_PROCESS.THICKNESS.main_thickness_thickness.id, mode: 'apply' });
    } else if (undoCommand) {
      const { resetHistoryUpTo } = useEditorStore.getState?.() || {};
      if (resetHistoryUpTo) resetHistoryUpTo(undoCommand);
      undoCommand.undo?.();
    }
  };


  // Applied state UI
  if (isApplied) {
    return (
      <div className={styles.toolCard}>
        <div className={styles.toolCardHeader}>
          <h3>Thickness</h3>
          {!isLoadedFromDraft && <p>☑️ Thickness Applied</p>}
          {isLoadedFromDraft && <p>☑️ Thickness Applied from Draft</p>}
        </div>
        <div className="flex flex-col gap-5 items-center mt-5 mb-1 pt-4 border-t border-gray-200">
          <button
            className={`px-2 py-1 rounded-md min-w-20 text-white ${
              isLoadedFromDraft
                ? 'bg-gray-400 cursor-not-allowed opacity-60'
                : 'bg-red-400 cursor-pointer hover:bg-red-500 hover:text-white'
            }`}
            onClick={isLoadedFromDraft ? undefined : onApply}
            disabled={isLoadedFromDraft}
            title={isLoadedFromDraft ? "Cannot unapply thickness loaded from draft" : "Unapply thickness"}
          >
            Unapply
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col absolute left-[10px] top-[10px] gap-3'>
      <div className='bg-white rounded-md p-2 shadow-[0_2px_8px_rgba(0,0,0,0.15)] w-[220px] z-[1000] max-h-[70vh]'>
        <div className={styles.toolCardHeader}>
          <h3>Thickness</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '10px 0' }}>
          {/* Offset Slider - Only visible for non-Mould socket type */}
          {!isMouldSelected && (
            <div>
              <DynamicSlider
                min={MIN_THICKNESS}
                max={MAX_THICKNESS}
                step={1}
                value={offsetValue}
                onChange={handleOffsetSliderChange}
                label="Offset"
                showValue={true}
                unit="mm"
                layout="default"
                className="w-full"
                labelClassName='text-gray-700 font-semibold'
              />
            </div>
          )}

          {/* First Slider - Always visible */}
          <div>
            <DynamicSlider
              min={MIN_THICKNESS}
              max={MAX_THICKNESS}
              step={1}
              value={thicknessValue}
              onChange={handleSliderChange}
              label={isMouldSelected ? "Thickness below trimeline" : "Thickness"}
              showValue={true}
              unit="mm"
              layout="default"
              className="w-full"
              labelClassName='text-gray-700 font-semibold'
            />
          </div>

          {/* Second Slider - Only visible for Mould socket type */}
          {isMouldSelected && (
            <div>
              <DynamicSlider
                min={MIN_THICKNESS}
                max={MAX_THICKNESS}
                step={1}
                value={topThicknessValue}
                onChange={handleTopSliderChange}
                label="Thickness above Trimeline"
                showValue={true}
                unit="mm"
                layout="default"
                className="w-full"
                labelClassName='text-gray-700 font-semibold'
              />
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col justify-between gap-3'>
        <button
          className={`py-1 rounded-md min-w-22 text-white text-lg cursor-pointer bg-violet-600 hover:text-white hover:bg-violet-500`}
          onClick={onApply}
        >
          Apply
        </button>
        <label className="flex items-center cursor-pointer select-none ml-1">
          <input
            type="checkbox"
            checked={thicknessState.previewVisible}
            onChange={() => setThickness({ previewVisible: !thicknessState.previewVisible })}
            className="sr-only"
          />
          <div className={`w-10 h-6 rounded-full transition-colors duration-200 relative ${thicknessState.previewVisible ? 'bg-purple-600' : 'bg-gray-300'}`}>
            <div
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 z-10 ${thicknessState.previewVisible ? 'translate-x-4' : 'translate-x-0'}`}
            ></div>
          </div>
          <span className={`ml-3 text-sm font-medium ${thicknessState.previewVisible ? 'text-gray-700' : 'text-gray-700'}`}>
            Show Preview
          </span>
        </label>
      </div>
    </div>
  );
};

export default ThicknessToolCard; 